import type {
  HomeScreenConfig,
  HomeScreenLayout,
  IconItem,
  SmplifyExtension,
  Target,
  FormFactor,
} from "./types";

const EMPTY_LAYOUT: HomeScreenLayout = { Pages: [[]], Dock: [] };

/** Read existing config and return the smplify extension (or a default). */
export function readExtension(config: HomeScreenConfig | undefined | null): SmplifyExtension {
  const ext = config?.["x-smplify"];
  if (ext && (ext.target === "iphone" || ext.target === "ipad" || ext.target === "both")) {
    return {
      target: ext.target,
      variants: {
        iphone: ext.variants?.iphone,
        ipad: ext.variants?.ipad,
      },
    };
  }
  // Backwards compat: if there's a top-level layout, treat it as iPhone variant.
  if (config && Array.isArray((config as HomeScreenLayout).Pages)) {
    return {
      target: "iphone",
      variants: {
        iphone: { Pages: config.Pages, Dock: config.Dock ?? [] },
      },
    };
  }
  return { target: "iphone", variants: { iphone: { ...EMPTY_LAYOUT } } };
}

export function getVariant(ext: SmplifyExtension, ff: FormFactor): HomeScreenLayout {
  return ext.variants[ff] ?? { Pages: [[]], Dock: [] };
}

/** Variant chosen to mirror to the top-level Apple shape. */
export function primaryFormFactor(target: Target): FormFactor {
  return target === "ipad" ? "ipad" : "iphone";
}

/**
 * Strip Smplify-only fields from items destined for the top-level Apple
 * `com.apple.homescreenlayout` payload. Apple's icon dictionary defines
 * exactly these keys: `Type`, `DisplayName`, `BundleID`, `URL`, `Pages`.
 * Anything else (notably `Icon` and our internal `IconKind`) is rejected
 * by the backend's strict JSON-schema validation (`additionalProperties:
 * false`). WebClip custom icons are configured via a separate
 * `com.apple.webClip.managed` payload, not in the home screen layout —
 * the favicon URL we resolve is editor-state only and re-derived on load.
 */
function stripSmplifyOnlyFields(items: IconItem[]): IconItem[] {
  return items.map((item) => {
    if (item.Type === "Folder") {
      return {
        ...item,
        Pages: item.Pages.map((page) => stripSmplifyOnlyFields(page)),
      };
    }
    if (item.Type === "WebClip") {
      // Drop both `Icon` (favicon URL — editor state only) and `IconKind`
      // (our favicon-vs-generic verdict). Apple's WebClip icon dictionary
      // for the home screen layout payload only accepts Type/URL/DisplayName.
      const { Icon: _icon, IconKind: _kind, ...rest } = item as IconItem & {
        Icon?: unknown;
        IconKind?: unknown;
      };
      return rest as IconItem;
    }
    return item;
  });
}

/**
 * Build the config object sent to the backend. The backend strictly validates
 * `com.apple.homescreenlayout` against Apple's schema and rejects ANY unknown
 * top-level keys (including the previous `x-smplify` vendor extension) with
 * "Validation access denied". We therefore emit only Apple-shaped fields.
 *
 * On reload the editor reconstructs its working state from the top-level
 * Pages/Dock via `readExtension`'s backwards-compat branch. The per-WebClip
 * IconKind verdict is intentionally not persisted on the wire — the favicon
 * probe re-resolves it the next time the editor mounts.
 */
export function buildConfig(ext: SmplifyExtension): HomeScreenConfig {
  const ff = primaryFormFactor(ext.target);
  const primary = getVariant(ext, ff);
  return {
    Pages: primary.Pages.map((page) => stripSmplifyOnlyFields(page)),
    Dock: stripSmplifyOnlyFields(primary.Dock ?? []),
  };
}

export function setVariant(
  ext: SmplifyExtension,
  ff: FormFactor,
  layout: HomeScreenLayout
): SmplifyExtension {
  return {
    ...ext,
    variants: { ...ext.variants, [ff]: layout },
  };
}

export function setTarget(ext: SmplifyExtension, target: Target): SmplifyExtension {
  // Preserve all stored variants; just switch which ones are active.
  const variants = { ...ext.variants };
  if (target === "iphone" || target === "both") {
    variants.iphone = variants.iphone ?? { Pages: [[]], Dock: [] };
  }
  if (target === "ipad" || target === "both") {
    variants.ipad = variants.ipad ?? { Pages: [[]], Dock: [] };
  }
  return { target, variants };
}
